import * as THREE from 'three';

/**
 * VR 校准用的真镜面（平面反射）。
 *
 * 踩过的两个坑：
 *  1. three 自带的 `Reflector` 在 WebXR 下是黑板：它内部再调一次 `renderer.render`，
 *     而 XR 会话里 renderer 永远用头显的立体相机，传入的虚拟相机被忽略。
 *  2. "把相机放在镜面上朝外拍" 不是镜子，只是另一台相机（会拍到桅杆/船头）。
 *
 * 所以这里自己做平面反射：
 *   - 用镜面平面构造反射矩阵，把真实相机的位置/朝向镜像过去 → 虚拟相机
 *   - 渲染时**临时把 renderer.xr.enabled 关掉**，虚拟相机才会被真正使用
 *   - 渲完恢复渲染目标与 XR 状态，并只渲染镜子前方（裁剪平面）
 */
export class SelfMirror {
  readonly group = new THREE.Group();

  private readonly target: THREE.WebGLRenderTarget;
  private readonly camera: THREE.PerspectiveCamera;
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;

  /** 渲染镜子这一帧时临时把"第一人称隐藏的头"显示出来 */
  onBeforeRender: (() => void) | null = null;
  onAfterRender: (() => void) | null = null;

  constructor(width = 0.9, height = 1.1) {
    this.target = new THREE.WebGLRenderTarget(768, 768, {
      colorSpace: THREE.SRGBColorSpace,
    });

    this.camera = new THREE.PerspectiveCamera(55, 1, 0.05, 60);

    // 画面左右翻转 = 镜像效果（贴图 repeat 取负、offset 补 1）
    this.target.texture.wrapS = THREE.RepeatWrapping;
    this.target.texture.repeat.x = -1;
    this.target.texture.offset.x = 1;

    this.material = new THREE.MeshBasicMaterial({ map: this.target.texture });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, height), this.material);
    this.mesh.name = 'calibration-mirror';
    this.mesh.position.set(0, 1.32, -1.72); // 挂回桅杆（固定机位，不用考虑反射相机距离了）
    this.mesh.rotation.x = -0.12; // 略朝下：坐着也能看到自己
    this.group.add(this.mesh);

    const frame = new THREE.Mesh(
      new THREE.BoxGeometry(width + 0.08, height + 0.08, 0.04),
      new THREE.MeshStandardMaterial({ color: 0x2a3138, roughness: 0.6, metalness: 0.15 }),
    );
    frame.position.copy(this.mesh.position);
    frame.rotation.copy(this.mesh.rotation);
    frame.position.z -= 0.03;
    this.group.add(frame);

    this.group.visible = false;
  }

  /**
   * 每帧：**固定机位**渲染 + 纹理水平翻转。
   *
   * 之前是"按真实相机做平面反射"，结果镜子画面会跟着头显一起转——
   * 那是反射相机，不是挂在船上的镜子。现在相机固定在镜面上、始终朝座位看，
   * 再靠贴图翻转得到镜像效果。
   */
  update(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void {
    if (!this.group.visible) return;

    this.mesh.updateWorldMatrix(true, false);
    const mirrorPos = this.mesh.getWorldPosition(new THREE.Vector3());
    const normal = new THREE.Vector3(0, 0, 1)
      .applyQuaternion(this.mesh.getWorldQuaternion(new THREE.Quaternion()))
      .normalize();

    // 固定朝向：从镜面看向"船体局部坐标里的座位中心"
    const parent = this.group.parent;
    const seatWorld = new THREE.Vector3(0, 1.05, 0.95);
    if (parent) parent.localToWorld(seatWorld);

    this.camera.scale.set(1, 1, 1);
    this.camera.position.copy(mirrorPos).addScaledVector(normal, 0.06);
    this.camera.up.set(0, 1, 0).applyQuaternion(this.mesh.getWorldQuaternion(new THREE.Quaternion()));
    this.camera.lookAt(seatWorld);

    this.camera.fov = 55;
    this.camera.aspect = 1;
    this.camera.near = 0.05;
    this.camera.far = 60;
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();

    this.onBeforeRender?.();

    const previousTarget = renderer.getRenderTarget();
    const xrWasEnabled = renderer.xr.enabled;
    try {
      // XR 会话里 renderer.render 会用头显相机，必须临时关掉
      renderer.xr.enabled = false;
      renderer.setRenderTarget(this.target);
      renderer.render(scene, this.camera);
    } finally {
      renderer.setRenderTarget(previousTarget);
      renderer.xr.enabled = xrWasEnabled;
      this.onAfterRender?.();
    }
  }

  /** 调试：把渲染结果原样贴到页面右下角，直接看镜子拍到了什么 */
  previewOverlay(renderer: THREE.WebGLRenderer): string {
    const size = 768;
    const buf = new Uint8Array(size * size * 4);
    renderer.readRenderTargetPixels(this.target, 0, 0, size, size, buf);

    // WebGL 像素是上下翻转的，这里翻回来
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-ctx';
    const img = ctx.createImageData(size, size);
    for (let y = 0; y < size; y++) {
      const src = (size - 1 - y) * size * 4;
      const dst = y * size * 4;
      for (let x = 0; x < size * 4; x++) img.data[dst + x] = buf[src + x] ?? 0;
    }
    ctx.putImageData(img, 0, 0);

    const url = canvas.toDataURL('image/png');
    let el = document.getElementById('mirror-preview') as HTMLImageElement | null;
    if (!el) {
      el = document.createElement('img');
      el.id = 'mirror-preview';
      el.style.cssText = 'position:fixed;right:8px;bottom:8px;width:240px;height:240px;border:2px solid #7cc7e0;z-index:9999;image-rendering:pixelated';
      document.body.appendChild(el);
    }
    el.src = url;
    return 'ok';
  }

  /** 调试：读渲染目标中心像素，确认不是全黑 */
  sample(renderer: THREE.WebGLRenderer): [number, number, number] | null {
    const buf = new Uint8Array(4);
    try {
      renderer.readRenderTargetPixels(this.target, 384, 384, 1, 1, buf);
      return [buf[0] ?? 0, buf[1] ?? 0, buf[2] ?? 0];
    } catch {
      return null;
    }
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  get visible(): boolean {
    return this.group.visible;
  }
}
